import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useScenarioStore } from '../store/useScenarioStore';
import { useAuthStore } from '../store/useAuthStore';
import { useAppModeStore } from '../store/useAppModeStore';
import HelpPanel from '../components/HelpPanel';
import { AccentCard } from '../components/design';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function ModeBadge({ mode }: { mode: 'individual' | 'fleet' }) {
  const isFleet = mode === 'fleet';
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        padding: '3px 8px',
        borderRadius: 3,
        background: isFleet ? 'var(--tco-neg-soft)' : 'var(--tco-line-soft)',
        border: `1px solid ${isFleet ? 'var(--tco-neg)' : 'var(--tco-line)'}33`,
        color: isFleet ? 'var(--tco-neg)' : 'var(--tco-ink-2)',
      }}
    >
      {isFleet ? 'Fleet' : 'Personal'}
    </span>
  );
}

export default function ScenariosPage() {
  const navigate = useNavigate();
  const { scenarios, loading, error, loadScenarios, deleteScenario, seedDemoScenario } =
    useScenarioStore();
  const { user } = useAuthStore();
  // Phase 24: filter the visible list to the active app mode. The list itself
  // still comes from the scenarios store (which fetches without mode filter
  // for backward compat); we slice client-side off the row's `mode` field.
  const activeMode = useAppModeStore((s) => s.activeMode);
  const isFleet = activeMode === 'fleet';
  const visibleScenarios = scenarios.filter(
    (s) => (s.mode === 'fleet' ? 'fleet' : 'individual') === activeMode,
  );
  const [showBanner, setShowBanner] = useState(true);
  // Resilience (UX audit P0): on a cold start the request can take ~50s. Surface
  // a bounded "waking up" hint after a few seconds so the user isn't left
  // staring at a bare "Loading…" with no idea whether anything is happening.
  const [slowLoad, setSlowLoad] = useState(false);

  useEffect(() => {
    loadScenarios();
  }, [loadScenarios]);

  useEffect(() => {
    if (!loading) {
      setSlowLoad(false);
      return;
    }
    const t = window.setTimeout(() => setSlowLoad(true), 6000);
    return () => window.clearTimeout(t);
  }, [loading]);

  useEffect(() => {
    if (user) {
      seedDemoScenario();
    }
  }, [user, seedDemoScenario]);

  function handleOpen(id: string | number, mode?: 'individual' | 'fleet') {
    const safeMode = mode === 'fleet' ? 'fleet' : 'individual';
    navigate(`/scenario/${safeMode}/${id}`);
  }

  async function handleDelete(id: string | number, name: string) {
    if (!window.confirm(`Delete scenario "${name}"? This cannot be undone.`)) return;
    await deleteScenario(id);
  }

  return (
    <div style={{ maxWidth: 1140, margin: '0 auto', padding: '32px 32px 48px' }}>
      {/* Beta notice */}
      {showBanner && !user && (
        <div
          style={{
            marginBottom: 18,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 16,
            background: 'var(--tco-neg-soft)',
            border: '1px solid var(--tco-neg)',
            color: 'var(--tco-neg)',
            borderRadius: 'var(--tco-radius)',
            padding: '12px 16px',
            fontSize: 13,
          }}
        >
          <span>
            <strong style={{ fontWeight: 600 }}>Beta notice:</strong> This is a shared environment.
            All scenarios are currently visible to all users. Do not save sensitive financial
            information. Per-user privacy is coming soon.
          </span>
          <button
            onClick={() => setShowBanner(false)}
            aria-label="Dismiss"
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--tco-neg)',
              cursor: 'pointer',
              fontSize: 14,
            }}
          >
            ✕
          </button>
        </div>
      )}

      {/* Help */}
      <HelpPanel />

      {/* Page header */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 28 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 28, fontWeight: 600, letterSpacing: '-0.015em', color: 'var(--tco-ink)' }}>
            {isFleet ? 'Fleet Scenarios' : 'Personal Scenarios'}
          </h1>
          <p style={{ margin: '6px 0 0', fontSize: 14, color: 'var(--tco-ink-2)' }}>
            {isFleet
              ? 'Commercial fleet TCO across heterogeneous line items.'
              : 'Compare vehicles side-by-side using total cost of ownership analysis.'}
          </p>
        </div>
        <button
          data-tour="new-scenario"
          onClick={() =>
            navigate(isFleet ? '/scenario/fleet/new' : '/scenario/individual/new')
          }
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            background: 'var(--tco-ink)',
            color: 'var(--tco-bg)',
            border: 'none',
            borderRadius: 6,
            padding: '9px 16px',
            fontSize: 14,
            fontWeight: 550,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          <span style={{
            display: 'inline-block',
            width: 9,
            height: 9,
            borderRadius: 1,
            background: 'var(--tco-accent)',
          }} />
          New scenario
        </button>
      </div>

      {/* Error — with a retry affordance. A bare error with no way forward was
          the outage failure mode; never leave the user stranded. */}
      {error && !loading && (
        <AccentCard stripe="red" padding="14px 16px" style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 13, color: 'var(--tco-neg)', marginBottom: 4 }}>
            Couldn't load your scenarios.
          </div>
          <div style={{ fontSize: 13, color: 'var(--tco-ink-2)', marginBottom: 10 }}>
            The server may be waking up (this can take about a minute on first
            request). {error}
          </div>
          <button
            onClick={() => loadScenarios()}
            style={{
              background: 'var(--tco-ink)',
              color: 'var(--tco-bg)',
              border: 'none',
              borderRadius: 6,
              padding: '7px 14px',
              fontSize: 13,
              fontWeight: 550,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Retry
          </button>
        </AccentCard>
      )}

      {/* Loading — bounded, with a waking-up hint once it runs long. */}
      {loading && (
        <div style={{ fontSize: 14, color: 'var(--tco-ink-3)' }}>
          {slowLoad
            ? 'Still working — the server may be waking up (~1 min). Hang tight…'
            : 'Loading scenarios…'}
        </div>
      )}

      {/* Phase 24: filter the list to the active mode. Suppressed on error so a
          failed load never masquerades as an empty (no-scenarios) state. */}
      {!loading && !error && visibleScenarios.length === 0 && (
        <div
          data-tour="scenarios-list"
          style={{
            textAlign: 'center',
            padding: '96px 24px',
            border: '1px dashed var(--tco-line)',
            borderRadius: 'var(--tco-radius)',
            background: 'var(--tco-surface)',
          }}
        >
          <p style={{ margin: '0 0 16px', color: 'var(--tco-ink-2)', fontSize: 14 }}>
            No {isFleet ? 'fleet' : 'personal'} scenarios yet.
          </p>
          <button
            onClick={() =>
              navigate(isFleet ? '/scenario/fleet/new' : '/scenario/individual/new')
            }
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--tco-neg)',
              fontSize: 14,
              fontWeight: 500,
              textDecoration: 'underline',
              textUnderlineOffset: 2,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Create your first {isFleet ? 'fleet' : 'personal'} scenario
          </button>
        </div>
      )}

      {/* Scenario card grid */}
      {!loading && visibleScenarios.length > 0 && (
        <div
          data-tour="scenarios-list"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            gap: 16,
          }}>
          {visibleScenarios.map((s) => {
            const mode = s.mode === 'fleet' ? 'fleet' : 'individual';
            return (
              <AccentCard
                key={s.id}
                stripe={mode === 'fleet' ? 'red' : 'ink'}
                padding="18px 20px"
                style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ marginBottom: 8 }}>
                    <ModeBadge mode={mode} />
                  </div>
                  <h2 style={{
                    margin: 0,
                    fontSize: 16,
                    fontWeight: 600,
                    color: 'var(--tco-ink)',
                    letterSpacing: '-0.005em',
                    lineHeight: 1.35,
                  }}>
                    {s.name}
                  </h2>
                  {s.description && (
                    <p style={{
                      margin: '6px 0 0',
                      fontSize: 13,
                      color: 'var(--tco-ink-2)',
                      lineHeight: 1.45,
                    }}>
                      {s.description}
                    </p>
                  )}
                </div>

                <div style={{ fontSize: 12, color: 'var(--tco-ink-3)' }}>
                  Updated {formatDate(s.updated_at)}
                </div>

                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  paddingTop: 12,
                  borderTop: '1px solid var(--tco-line-soft)',
                }}>
                  <button
                    onClick={() => handleOpen(s.id, mode)}
                    style={{
                      flex: 1,
                      background: 'var(--tco-ink)',
                      color: 'var(--tco-bg)',
                      border: 'none',
                      borderRadius: 6,
                      padding: '7px 12px',
                      fontSize: 13,
                      fontWeight: 550,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    Open
                  </button>
                  <button
                    onClick={() => handleDelete(s.id, s.name)}
                    title="Delete scenario"
                    style={{
                      background: 'transparent',
                      border: '1px solid var(--tco-line)',
                      color: 'var(--tco-ink-3)',
                      borderRadius: 6,
                      padding: '7px 10px',
                      cursor: 'pointer',
                    }}
                  >
                    <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                </div>
              </AccentCard>
            );
          })}
        </div>
      )}

    </div>
  );
}
